
import {
  BellIcon,
  CheckIcon,
  ClipboardListIcon,
  HeadsetIcon,
  StickyNoteIcon,
} from "lucide-react"

import { cn } from "../../lib/utils"

/** One sample WhatsApp-style piece (Mailmodo widget-collage pattern). Built in code; the use-case cards around it carry no customer names. */
export function JobWidget({ w, className }) {
  switch (w.kind) {
    case "in":
      return (
        <div
          className={cn(
            "max-w-[300px] rounded-2xl rounded-bl-md border border-border bg-background px-4 py-2.5 text-sm shadow-card",
            className
          )}
        >
          {w.text}
          {w.time && (
            <span className="ml-2 text-[11px] text-muted-foreground">
              {w.time}
            </span>
          )}
        </div>
      )

    case "out":
      return (
        <div
          className={cn(
            "max-w-[320px] rounded-2xl rounded-br-md bg-brand-tint px-4 py-3 text-sm",
            className
          )}
        >
          <p>{w.text}</p>

          {w.buttons && (
            <ul className="mt-2.5 flex flex-wrap gap-1.5">
              {w.buttons.map((b) => (
                <li
                  key={b}
                  className="rounded-md border border-primary bg-background px-2.5 py-1 text-xs font-semibold text-brand-text"
                >
                  {b}
                </li>
              ))}
            </ul>
          )}
        </div>
      )

    case "form":
      return (
        <div
          className={cn(
            "w-full max-w-[300px] rounded-xl border border-border bg-background p-4 text-sm shadow-card",
            className
          )}
        >
          <p className="mb-3 flex items-center gap-2 font-semibold">
            <ClipboardListIcon
              className="size-4 text-brand-text"
              aria-hidden="true"
            />
            {w.title}
          </p>

          <dl className="flex flex-col gap-1.5">
            {w.rows.map(([k, v]) => (
              <div
                key={k}
                className="flex justify-between gap-3 border-b border-border pb-1.5 last:border-0"
              >
                <dt className="text-muted-foreground">{k}</dt>
                <dd className="font-medium">{v}</dd>
              </div>
            ))}
          </dl>

          <p className="mt-3 inline-flex items-center gap-1 rounded-md bg-brand-tint px-2 py-0.5 text-xs font-semibold text-brand-text">
            <CheckIcon className="size-3.5" aria-hidden="true" />
            {w.state}
          </p>
        </div>
      )

    case "reminder":
      return (
        <div
          className={cn(
            "w-full max-w-[320px] rounded-xl border border-border bg-background p-4 text-sm shadow-card",
            className
          )}
        >
          <p className="mb-2 flex items-center gap-2 font-semibold">
            <BellIcon
              className="size-4 text-brand-text"
              aria-hidden="true"
            />
            {w.title}
          </p>

          <p className="text-ink-strong">"{w.text}"</p>

          <p className="mt-3 inline-flex rounded-md bg-brand-tint px-2 py-0.5 text-xs font-semibold text-brand-text">
            {w.state}
          </p>
        </div>
      )

    case "lead":
      return (
        <div
          className={cn(
            "w-full max-w-[340px] rounded-xl border border-border bg-background p-4 text-sm shadow-card",
            className
          )}
        >
          <p className="font-semibold">{w.title}</p>

          <ul className="mt-2.5 flex flex-wrap gap-1.5" aria-label="Lead status">
            {w.statuses.map((s) => (
              <li
                key={s}
                aria-current={s === w.active ? "true" : undefined}
                className={cn(
                  "rounded-md border px-2 py-0.5 text-xs font-semibold",
                  s === w.active
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border text-muted-foreground"
                )}
              >
                {s}
              </li>
            ))}
          </ul>

          <p className="mt-3 flex gap-2 rounded-lg bg-surface-cream p-2.5 text-xs leading-relaxed text-ink-strong">
            <StickyNoteIcon
              className="mt-0.5 size-3.5 shrink-0 text-brand-text"
              aria-hidden="true"
            />
            {w.note}
          </p>
        </div>
      )

    case "agent":
      return (
        <p
          className={cn(
            "inline-flex items-center gap-2 rounded-lg bg-charcoal px-3 py-2 text-xs font-medium text-on-dark",
            className
          )}
        >
          <HeadsetIcon
            className="size-3.5 text-brand-bright"
            aria-hidden="true"
          />
          {w.text}
        </p>
      )

    case "chip":
      return (
        <p
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md bg-brand-tint px-2.5 py-1 text-xs font-semibold text-brand-text",
            className
          )}
        >
          <CheckIcon className="size-3.5" aria-hidden="true" />
          {w.text}
        </p>
      )

    default:
      return null
  }
}
